import { useEffect } from "react";
import { Link, useMatch } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ClipboardList, Home, Info, LucideIcon, MapPin, UserRound } from "lucide-react";
import { loadTenantConfig } from "../../../redux/slice/config/configSlice";
import { useAppDispatch } from "../../../redux/useDispatch";
import { RootState } from "../../../redux/store";
import routes from "../../../routes/routes";
import { cn } from "../../../util/cn";

type ToolbarItem = {
  name: string;
  route: string;
  icon: LucideIcon;
};

const toolbarItems: Record<string, ToolbarItem> = {
  home: { icon: Home, route: routes.home, name: "home" },
  locations: { icon: MapPin, route: routes.locations, name: "locations" },
  orders: { icon: ClipboardList, route: routes.orders, name: "orders" },
  info: { icon: Info, route: routes.hints, name: "info" },
};

const Toolbar = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const tenantToolbarItems = useSelector(
    (state: RootState) => state.tenantConfig.config.toolbarItems
  );
  // H-3a account entry point — rendered only when the tenant flag is on
  // (dark-shipped: false everywhere until the backend exists).
  const accountsEnabled = useSelector(
    (state: RootState) => state.tenantConfig.config.accountsEnabled === true
  );
  const accountProfile = useSelector(
    (state: RootState) => state.account.profile
  );

  const locationsTabActive = Boolean(useMatch(routes.locations));
  const ordersTabActive = Boolean(useMatch(routes.orders));
  const hintsTabActive = Boolean(useMatch(routes.hints));
  const homeTabActive = Boolean(useMatch(routes.home));
  const accountMatch = Boolean(useMatch(routes.accountPath));
  const accountLoginMatch = Boolean(useMatch(routes.accountLoginPath));
  const accountLoginVerifyMatch = Boolean(
    useMatch(routes.accountLoginVerifyPath)
  );
  const accountTabActive =
    accountMatch || accountLoginMatch || accountLoginVerifyMatch;

  useEffect(() => {
    dispatch(loadTenantConfig());
  }, [dispatch]);

  const isItemActive = (item: string) =>
    (homeTabActive && item === "home") ||
    (locationsTabActive && item === "locations") ||
    (ordersTabActive && item === "orders") ||
    (hintsTabActive && item === "info");

  return (
    <nav id="toolbar" className="mx-auto h-[59px] max-w-md bg-background">
      <div className="flex h-full">
        {tenantToolbarItems?.map((item: string) => {
          const entry = toolbarItems[item];
          if (!entry) return null;

          const isActive = isItemActive(item);
          const Icon = entry.icon;

          return (
            <Link
              key={entry.route}
              to={entry.route}
              className={cn(
                "flex min-w-0 flex-1 flex-col items-center justify-center gap-1 transition-colors",
                isActive
                  ? "bg-primary text-onPrimary"
                  : "text-foreground/60 hover:text-foreground"
              )}
            >
              <Icon className="h-6 w-6" aria-hidden />
              <span
                className={cn(
                  "w-full truncate px-0.5 text-center text-xs leading-tight",
                  isActive ? "font-semibold" : "font-normal"
                )}
              >
                {t(`toolbar.${entry.name}`)}
              </span>
            </Link>
          );
        })}
        {accountsEnabled && (
          <Link
            to={accountProfile ? routes.accountPath : routes.accountLoginPath}
            className={cn(
              "flex min-w-0 flex-1 flex-col items-center justify-center gap-1 transition-colors",
              accountTabActive
                ? "bg-primary text-onPrimary"
                : "text-foreground/60 hover:text-foreground"
            )}
          >
            <UserRound className="h-6 w-6" aria-hidden />
            <span
              className={cn(
                "w-full truncate px-0.5 text-center text-xs leading-tight",
                accountTabActive ? "font-semibold" : "font-normal"
              )}
            >
              {t("account.menu_label")}
            </span>
          </Link>
        )}
      </div>
    </nav>
  );
};

export default Toolbar;
